import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { BRANDING } from '../config/branding';
import { 
  LogOut, 
  AlertTriangle, 
  CheckCircle2, 
  Printer, 
  Calendar, 
  Users, 
  CreditCard,
  ShoppingBag,
  Receipt,
  X,
  Loader2
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatCurrency, toNumber } from '../utils/formatters';

export default function CheckOutDialog({ open, onOpenChange, reservation, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [checkoutData, setCheckoutData] = useState(null);
  const [fullReservation, setFullReservation] = useState(null);
  const [sales, setSales] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [settings, setSettings] = useState({});
  const printRef = useRef();

  useEffect(() => {
    if (open && reservation?.id) {
      fetchCheckoutData();
      fetchSettings();
    }
  }, [open, reservation?.id]);

  const fetchCheckoutData = async () => {
    setLoading(true);
    try {
      // Get full reservation details
      const reservationRes = await api.get(`/reservations/${reservation.id}`);
      setFullReservation(reservationRes.data);

      // Get checkout status (includes unpaid sales check)
      const statusRes = await api.get(`/reservations/${reservation.id}/checkout`);
      setCheckoutData(statusRes.data);

      // Get all sales for this reservation
      const salesRes = await api.get(`/sales?reservation_id=${reservation.id}`);
      setSales(salesRes.data.data || salesRes.data.sales || salesRes.data || []);
    } catch (error) {
      console.error('Error fetching checkout data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      const settingsMap = {};
      response.data.forEach(s => {
        settingsMap[s.key] = s.value;
      });
      setSettings(settingsMap);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleConfirmCheckout = async () => {
    if (checkoutData?.hasUnpaidSales) {
      toast.error('Impossible de valider le check-out avec des ventes impayées');
      return;
    }

    setConfirming(true);
    try {
      await api.post(`/reservations/${reservation.id}/checkout`);
      toast.success('Check-out validé avec succès !');
      setShowReceipt(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors du check-out');
    } finally {
      setConfirming(false);
    }
  };

  const handlePrintReceipt = () => {
    const printContent = printRef.current;
    const WinPrint = window.open('', '', 'width=800,height=900');
    WinPrint.document.write(`
      <html>
        <head>
          <title>Récapitulatif de séjour</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #333; }
            .receipt { max-width: 210mm; margin: 0 auto; padding: 15mm; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #16a34a; }
            .logo { font-size: 24px; font-weight: bold; color: #16a34a; margin-bottom: 5px; }
            .company-info { color: #666; font-size: 11px; }
            .title { text-align: center; margin: 20px 0; }
            .title h1 { font-size: 20px; color: #1a1a1a; }
            .title .subtitle { color: #666; margin-top: 5px; }
            .section { margin-bottom: 25px; }
            .section-title { font-weight: bold; color: #16a34a; font-size: 14px; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #e5e5e5; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
            .info-item { }
            .info-label { color: #666; font-size: 10px; text-transform: uppercase; }
            .info-value { font-weight: 500; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background: #f5f5f5; padding: 10px; text-align: left; font-weight: 600; border-bottom: 2px solid #e5e5e5; font-size: 11px; }
            td { padding: 10px; border-bottom: 1px solid #e5e5e5; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .totals { margin-top: 20px; display: flex; justify-content: flex-end; }
            .totals-table { width: 280px; }
            .totals-table td { padding: 8px; }
            .totals-table .label { color: #666; }
            .totals-table .value { text-align: right; font-weight: 500; }
            .grand-total { font-size: 16px; font-weight: bold; background: #f0fdf4; }
            .grand-total td { border-top: 2px solid #16a34a; }
            .status-paid { color: #16a34a; font-weight: 500; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e5e5; text-align: center; color: #666; font-size: 10px; }
            .thank-you { font-size: 14px; color: #16a34a; font-weight: 500; margin-bottom: 10px; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    WinPrint.document.close();
    WinPrint.focus();
    setTimeout(() => {
      WinPrint.print();
      WinPrint.close();
    }, 250);
  };

  const handleClose = () => {
    if (showReceipt) {
      onSuccess?.();
    }
    setShowReceipt(false);
    onOpenChange(false);
  };

  if (!reservation) return null;

  // Use full reservation data if available, otherwise fallback to prop
  const res = fullReservation || reservation;

  // Ensure dates are valid
  const checkInDate = res.check_in ? new Date(res.check_in) : null;
  const checkOutDate = res.check_out ? new Date(res.check_out) : null;
  const isValidDates = checkInDate && checkOutDate && !isNaN(checkInDate) && !isNaN(checkOutDate);
  
  const nights = isValidDates ? differenceInDays(checkOutDate, checkInDate) : 0;
  const salesArray = Array.isArray(sales) ? sales : [];
  const paidSales = salesArray.filter(s => s.paid || s.status === 'PAID');
  const unpaidSales = salesArray.filter(s => !s.paid && s.status !== 'PAID');
  const totalExtras = salesArray.reduce((sum, s) => sum + toNumber(s.total), 0);
  const totalPaidExtras = paidSales.reduce((sum, s) => sum + toNumber(s.total), 0);
  const totalUnpaidExtras = unpaidSales.reduce((sum, s) => sum + toNumber(s.total), 0);
  const grandTotal = toNumber(res.total_amount) + totalExtras;

  // Helper function to safely format date
  const safeFormatDate = (date, formatStr, options = {}) => {
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return 'Date invalide';
      return format(d, formatStr, options);
    } catch {
      return 'Date invalide';
    }
  };

  // Receipt view after successful checkout
  if (showReceipt) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-5 h-5" />
              Check-out validé - Récapitulatif de séjour
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-2 mb-4">
            <Button onClick={handlePrintReceipt} className="bg-green-600 hover:bg-green-700">
              <Printer className="w-4 h-4 mr-2" />
              Imprimer le récapitulatif
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Fermer
            </Button>
          </div>

          <div className="flex-1 overflow-auto bg-gray-100 p-4 rounded-lg">
            <div ref={printRef} className="bg-white shadow-lg mx-auto p-8" style={{ maxWidth: '210mm' }}>
              <div className="receipt">
                {/* Header */}
                <div className="header" style={{ textAlign: 'center', marginBottom: '30px', paddingBottom: '20px', borderBottom: '2px solid #16a34a' }}>
                  <div className="logo" style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a', marginBottom: '5px' }}>
                    {settings.company_name || BRANDING.name}
                  </div>
                  <div className="company-info" style={{ color: '#666', fontSize: '11px' }}>
                    {settings.company_address && <div>{settings.company_address}</div>}
                    {settings.company_phone && <span>Tél: {settings.company_phone}</span>}
                    {settings.company_email && <span> | {settings.company_email}</span>}
                  </div>
                </div>

                {/* Title */}
                <div className="title" style={{ textAlign: 'center', margin: '20px 0' }}>
                  <h1 style={{ fontSize: '20px', color: '#1a1a1a' }}>RÉCAPITULATIF DE SÉJOUR</h1>
                  <div className="subtitle" style={{ color: '#666', marginTop: '5px' }}>
                    N° {res.platform_ref || `R-${res.id}`} | {safeFormatDate(new Date(), 'dd MMMM yyyy', { locale: fr })}
                  </div>
                </div>

                {/* Guest & Stay Info */}
                <div className="section" style={{ marginBottom: '25px' }}>
                  <div className="section-title" style={{ fontWeight: 'bold', color: '#16a34a', fontSize: '14px', marginBottom: '10px', paddingBottom: '5px', borderBottom: '1px solid #e5e5e5' }}>
                    Informations du séjour
                  </div>
                  <div className="info-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div className="info-item">
                      <div className="info-label" style={{ color: '#666', fontSize: '10px', textTransform: 'uppercase' }}>Client</div>
                      <div className="info-value" style={{ fontWeight: '500' }}>
                        {res.guest?.first_name} {res.guest?.last_name}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label" style={{ color: '#666', fontSize: '10px', textTransform: 'uppercase' }}>Arrivée</div>
                      <div className="info-value" style={{ fontWeight: '500' }}>
                        {safeFormatDate(res.check_in, 'EEEE dd MMMM yyyy', { locale: fr })}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label" style={{ color: '#666', fontSize: '10px', textTransform: 'uppercase' }}>Occupants</div>
                      <div className="info-value" style={{ fontWeight: '500' }}>
                        {res.adults} adulte(s){res.children > 0 && `, ${res.children} enfant(s)`}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label" style={{ color: '#666', fontSize: '10px', textTransform: 'uppercase' }}>Départ</div>
                      <div className="info-value" style={{ fontWeight: '500' }}>
                        {safeFormatDate(res.check_out, 'EEEE dd MMMM yyyy', { locale: fr })}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label" style={{ color: '#666', fontSize: '10px', textTransform: 'uppercase' }}>Source</div>
                      <div className="info-value" style={{ fontWeight: '500' }}>{res.source}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label" style={{ color: '#666', fontSize: '10px', textTransform: 'uppercase' }}>Durée</div>
                      <div className="info-value" style={{ fontWeight: '500' }}>{nights} nuit(s)</div>
                    </div>
                  </div>
                </div>

                {/* Extras - Detailed */}
                {salesArray.length > 0 ? (
                  <div className="section" style={{ marginBottom: '25px' }}>
                    <div className="section-title" style={{ fontWeight: 'bold', color: '#16a34a', fontSize: '14px', marginBottom: '10px', paddingBottom: '5px', borderBottom: '1px solid #e5e5e5' }}>
                      Détail des consommations sur place
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                      <thead>
                        <tr>
                          <th style={{ background: '#f5f5f5', padding: '10px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e5e5e5', fontSize: '11px' }}>Article</th>
                          <th style={{ background: '#f5f5f5', padding: '10px', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid #e5e5e5', fontSize: '11px', width: '60px' }}>Qté</th>
                          <th style={{ background: '#f5f5f5', padding: '10px', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid #e5e5e5', fontSize: '11px', width: '100px' }}>P.U.</th>
                          <th style={{ background: '#f5f5f5', padding: '10px', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid #e5e5e5', fontSize: '11px', width: '100px' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesArray.flatMap((sale, saleIdx) => 
                          sale.lines?.map((line, lineIdx) => (
                            <tr key={`${saleIdx}-${lineIdx}`}>
                              <td style={{ padding: '10px', borderBottom: '1px solid #e5e5e5' }}>
                                {line.product_name || line.product?.name || 'Article'}
                                <br/>
                                <span style={{ color: '#666', fontSize: '10px' }}>
                                  {safeFormatDate(sale.sale_date || sale.created_at, 'dd/MM/yyyy HH:mm')}
                                </span>
                              </td>
                              <td style={{ padding: '10px', borderBottom: '1px solid #e5e5e5', textAlign: 'center' }}>{line.quantity}</td>
                              <td style={{ padding: '10px', borderBottom: '1px solid #e5e5e5', textAlign: 'right' }}>{formatCurrency(line.unit_price)}</td>
                              <td style={{ padding: '10px', borderBottom: '1px solid #e5e5e5', textAlign: 'right' }}>{formatCurrency(line.line_total)}</td>
                            </tr>
                          )) || []
                        )}
                      </tbody>
                      <tfoot>
                        <tr style={{ fontWeight: 'bold', background: '#f0fdf4' }}>
                          <td colSpan="3" style={{ padding: '12px', borderTop: '2px solid #16a34a', textAlign: 'right' }}>TOTAL EXTRAS</td>
                          <td style={{ padding: '12px', borderTop: '2px solid #16a34a', textAlign: 'right' }}>{formatCurrency(totalExtras)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="section" style={{ marginBottom: '25px', textAlign: 'center', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
                    <p style={{ color: '#666', fontStyle: 'italic' }}>Aucune consommation sur place</p>
                  </div>
                )}

                {/* Footer */}
                <div className="footer" style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #e5e5e5', textAlign: 'center', color: '#666', fontSize: '10px' }}>
                  <div className="thank-you" style={{ fontSize: '14px', color: '#16a34a', fontWeight: '500', marginBottom: '10px' }}>
                    Merci pour votre séjour ! À bientôt !
                  </div>
                  {settings.invoice_footer ? (
                    <p style={{ whiteSpace: 'pre-line' }}>{settings.invoice_footer}</p>
                  ) : (
                    <p>{settings.company_name || BRANDING.name} - {settings.company_address || ''}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Main checkout validation dialog
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogOut className="w-5 h-5" />
            Check-out - {res.guest?.first_name} {res.guest?.last_name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Reservation Summary */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">Récapitulatif du séjour</h3>
                    <p className="text-sm text-muted-foreground">Réf: {res.platform_ref || `#${res.id}`}</p>
                  </div>
                  <Badge variant={res.source === 'DIRECT' ? 'default' : 'secondary'}>
                    {res.source}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <span className="text-muted-foreground">Arrivée:</span>{' '}
                      <span className="font-medium">{safeFormatDate(res.check_in, 'dd/MM/yyyy')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <span className="text-muted-foreground">Départ:</span>{' '}
                      <span className="font-medium">{safeFormatDate(res.check_out, 'dd/MM/yyyy')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <span className="font-medium">{res.adults}</span> adulte(s)
                      {res.children > 0 && <span>, <span className="font-medium">{res.children}</span> enfant(s)</span>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Extras / Sales - Detailed */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4" />
                    Consommations sur place
                  </h3>
                  <Badge variant="outline">{salesArray.length} ticket(s)</Badge>
                </div>

                {salesArray.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Aucune consommation enregistrée pendant le séjour
                  </p>
                ) : (
                  <div className="space-y-3">
                    {salesArray.map((sale) => (
                      <div 
                        key={sale.id} 
                        className={`rounded-lg border ${
                          sale.paid || sale.status === 'PAID' ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'
                        }`}
                      >
                        {/* Sale header */}
                        <div className="flex items-center justify-between p-3 border-b border-inherit">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">
                              Ticket #{sale.sale_number || sale.id}
                            </p>
                            <span className="text-xs text-muted-foreground">
                              {safeFormatDate(sale.sale_date || sale.created_at, 'dd/MM HH:mm')}
                            </span>
                          </div>
                          {sale.paid || sale.status === 'PAID' ? (
                            <Badge variant="default" className="bg-green-600 text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Payé
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Non payé
                            </Badge>
                          )}
                        </div>
                        {/* Sale lines */}
                        <div className="p-3 space-y-1">
                          {sale.lines?.map((line, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>
                                {line.quantity}x {line.product_name || line.product?.name || 'Article'}
                              </span>
                              <span className="font-medium">{formatCurrency(line.line_total)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between pt-2 border-t border-inherit font-semibold">
                            <span>Total ticket</span>
                            <span>{formatCurrency(sale.total)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unpaid Warning */}
            {checkoutData?.hasUnpaidSales && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-red-800">Paiement requis</h4>
                    <p className="text-sm text-red-700 mt-1">
                      {unpaidSales.length} ticket(s) non payé(s) pour un total de{' '}
                      <span className="font-bold">{formatCurrency(totalUnpaidExtras)}</span>
                    </p>
                    <p className="text-sm text-red-600 mt-2">
                      Veuillez encaisser ces ventes avant de valider le check-out.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Totals Summary - Only extras */}
            {totalExtras > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Extras payés</span>
                    <span className="text-green-600">{formatCurrency(totalPaidExtras)}</span>
                  </div>
                  {totalUnpaidExtras > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Extras impayés</span>
                      <span className="text-red-600">{formatCurrency(totalUnpaidExtras)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total consommations</span>
                    <span>{formatCurrency(totalExtras)}</span>
                  </div>
                </div>
              </>
            )}

            {/* Action Buttons */}
            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button variant="outline" onClick={handleClose}>
                Annuler
              </Button>
              <Button 
                onClick={handleConfirmCheckout}
                disabled={checkoutData?.hasUnpaidSales || confirming}
                className={checkoutData?.hasUnpaidSales ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}
                data-testid="confirm-checkout-btn"
              >
                {confirming ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Receipt className="w-4 h-4 mr-2" />
                )}
                {checkoutData?.hasUnpaidSales 
                  ? 'Paiement requis' 
                  : 'Valider le check-out & Imprimer'
                }
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
